import { Button } from '@insurancemenu/imenu-components-lib';
import { IconCheckmark } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  ChecklistButtonStateText,
  ChecklistCard,
  ChecklistCompletionStatus,
  ChecklistOption,
  EnrollmentStage,
} from 'types/enrollmentService';
import {
  CARD_DATA,
  isEnrollmentPeriodNotEditable,
  isEnrollmentFilesStep,
} from 'global/checklists-helpers/ChecklistHelper';
import { toJS } from 'mobx';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { employerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { createStyles } from './styles';

type CardStatusesTitlesObjectType = {
  [K in ChecklistCompletionStatus]: string | JSX.Element;
};

type CardButtonStateObjectType = {
  [K in ChecklistButtonStateText]: string;
};

const BUTTON_STATE_TEXT: CardButtonStateObjectType = Object.freeze({
  VIEW_EDIT: 'View / Edit',
  VIEW: 'View',
  UNAVAILABLE: 'Unavailable',
  CONTINUE: 'Continue',
  REVIEW_SUBMIT: 'Review and Submit',
  PROCESSING: 'Processing',
});

interface OptionCardProps {
  option: ChecklistOption<ChecklistCard>;
  enrollmentPeriodId: string;
  enrollmentStage: EnrollmentStage;
  groupId: string;
  cartId?: string;
}

/**
 * @deprecated: Use StepCard component instead.
 */
const OptionCard: React.FC<OptionCardProps> = observer(
  ({ enrollmentStage, option: { card, completionStatus, buttonState }, enrollmentPeriodId, groupId, cartId }) => {
    const history = useHistory();
    const {
      userStore: { userRoles },
    } = useRootStore();

    const convertedRoles = userRoles.map((role) => toJS(role)).slice();
    const isEmployer = employerRights(convertedRoles);
    const isFilesStepCard = isEnrollmentFilesStep(card);

    function onRedirect(): void {
      /**
       * Making this local copy of the object instead of accessing it twice is better for two reasons:
       * 1) More optimized code (which in current situation doesn't make a bit difference but still);
       * 2) Theoretically CARD_DATA[card] may be different between the two calls in the function. In this case
       * there might be a astonishingly hard-to-find bug, so you better stick to such approach as below.
       */
      const cardDataObject = CARD_DATA[card];
      if (cardDataObject.redirectTo) {
        history.push(cardDataObject.redirectTo(enrollmentPeriodId, groupId, cartId));
      }
    }

    const classes = createStyles();
    const isPeriodNotEditable = isEnrollmentPeriodNotEditable(enrollmentStage);
    const isCensusStepDisabled = card === ChecklistCard.CENSUS_IMPORT && isPeriodNotEditable;

    const roleCheck = CARD_DATA[card]?.isAvailable;
    const isStepAvailable = roleCheck ? roleCheck(convertedRoles) : true;

    const ContinueButton = (
      <Button color="primary" size="large" onClick={onRedirect} className={classes.actionButton}>
        {BUTTON_STATE_TEXT[buttonState]}
      </Button>
    );

    const ViewEditButton = (
      <Button
        type="opaque"
        color="primary"
        disabled={isCensusStepDisabled}
        size="large"
        onClick={onRedirect}
        className={classes.actionButton}
      >
        {BUTTON_STATE_TEXT[buttonState]}
      </Button>
    );

    const UnavailableButton = (
      <Button size="large" disabled className={classes.actionButton}>
        {BUTTON_STATE_TEXT[buttonState]}
      </Button>
    );

    function renderActionButton(): React.ReactNode {
      switch (buttonState) {
        case ChecklistButtonStateText.UNAVAILABLE:
        case ChecklistButtonStateText.PROCESSING:
          return UnavailableButton;
        case ChecklistButtonStateText.VIEW:
        case ChecklistButtonStateText.VIEW_EDIT:
          return ViewEditButton;
        case ChecklistButtonStateText.CONTINUE:
          return ContinueButton;

        default:
          return null;
      }
    }

    const STATUSES_TITLES_OBJECT: CardStatusesTitlesObjectType = Object.freeze({
      COMPLETED: (
        <span className={classes.completedCardWrapper}>
          <IconCheckmark className={classes.completedIcon} width="1.8rem" />
          <span className={classes.cardStatusText}>Completed</span>
        </span>
      ),
      READY_TO_BEGIN: (
        <span className={clsx(classes.cardStatusText, classes.readyToBeginStatusText)}>Ready to begin</span>
      ),
      CANNOT_COMPLETE_YET: <span className={classes.cardStatusText}>Cannot complete yet</span>,
      IN_PROGRESS: <span className={clsx(classes.cardStatusText, classes.inProgressStatusText)}>In-Progress</span>,
    });

    const cardTitle =
      isEmployer && CARD_DATA[card]?.employerTitle ? CARD_DATA[card].employerTitle : CARD_DATA[card].title;

    return (
      <div className={clsx(classes.card, isFilesStepCard && classes.filesStepCard)}>
        <div>
          <h3 className={classes.title}>{cardTitle}</h3>
          <p className={classes.text}>{CARD_DATA[card].text}</p>
        </div>
        <div className={classes.rightSection}>
          {isStepAvailable ? (
            <>
              {renderActionButton()} {STATUSES_TITLES_OBJECT[completionStatus]}
            </>
          ) : (
            <>
              <Button size="large" disabled className={classes.actionButton}>
                Internal Use Only
              </Button>
              <span>{STATUSES_TITLES_OBJECT[completionStatus]}</span>
            </>
          )}
        </div>
      </div>
    );
  },
);

export default OptionCard;
